import { handle } from 'redux-pack';
import { assoc } from 'ramda';

import { get, patch } from 'lib/communication';
import createReducer from 'lib/createReducer';
import { doctorRole, neuropsychologistRole } from '../../../project.config';

/* Action types */

const LOAD = 'myms/session/LOAD';
const LOAD_SETTINGS = 'myms/session/LOAD_SETTINGS';
const UPDATE_LANG = 'myms/session/UPDATE_LANG';
const SET_SESSION_DATA = 'mystellarq/session/SET_SESSION_DATA';

export const actionTypes = { LOAD, LOAD_SETTINGS, SET_SESSION_DATA };

/* Action creator(s) */

const loadSession = () => ({
  type: LOAD,
  promise: get('/state'),
});

const loadSettings = () => ({
  type: LOAD_SETTINGS,
  promise: get('/vault/v2/user/'),
});

const updateLanguage = lang => ({
  type: UPDATE_LANG,
  meta: { lang },
  promise: patch('/vault/v2/user/', { uiLang: lang }, {}, false),
});

const setSessionData = payload => ({
  type: SET_SESSION_DATA,
  payload,
});

export const actions = { loadSession, loadSettings, updateLanguage, setSessionData };

/* Handlers and the reducer */

const initialState = {
  lang: null,
  isLoading: false,
};

// This function maps and merges the payload to MyMS state
// It is implemented to ease support for JWT based Neuro auth.
// TODO: See pic and lang later, they are not currently provided.
const mergePayloadToDate = payload => state => ({
  ...state,
  id: payload.id,
  role: payload.role,
  citizenId: payload.ssn,
  firstName: payload.firstNames,
  lastName: payload.lastNames,
  external: payload.external,
  address: payload.address,
});

const mapJWTToSession = (jwt, state) => ({
  ...state,
  id: jwt.patientid,
  userid: jwt.useruuid,
  groups: jwt.usergroups,
  citizenId: jwt.patientssn,
  firstName: jwt.patientfirstnames,
  role: chooseRole(jwt),
  lastName: jwt.patientlastnames,
  delegated: jwt.delegated,
  pic: null,
  clorgid: jwt.clorgid,
});

const handleLoadSession = (s, a) =>
  handle(s, a, {
    start: assoc('isLoading', true),
    finish: assoc('isLoading', false),
    failure: assoc('error', a.payload),
    success: mergePayloadToDate(a.payload),
  });

const handleSetSessionData = (s, a) => mapJWTToSession(a.payload, s);

const handleLoadSettings = (s, a) =>
  handle(s, a, {
    start: assoc('isLoading', true),
    finish: assoc('isLoading', false),
    failure: assoc('error', a.payload),
    success: prevState => ({ ...prevState, lang: a.payload.uILanguage }),
  });

const handleUpdateLang = (s, a) =>
  handle(s, a, {
    start: assoc('isLoading', true),
    finish: assoc('isLoading', false),
    failure: assoc('error', 'failed to update language'),
    success: assoc('lang', a.meta.lang),
  });

/**
 * Choose a _role_ based on _usergroups_ in JWT.
 *
 * Default is `"PATIENT"`. `"NEUROPSYCHOLOGIST"` takes precedence over `"DOCTOR"`.
 */
function chooseRole(jwt) {
  let role = 'PATIENT';
  if (!Array.isArray(jwt.usergroups)) return role;
  else if (jwt.usergroups.includes(neuropsychologistRole)) return 'NEUROPSYCHOLOGIST';
  else if (jwt.usergroups.includes(doctorRole)) return 'DOCTOR';
  else return role;
}

export default createReducer(initialState, {
  [LOAD]: handleLoadSession,
  [LOAD_SETTINGS]: handleLoadSettings,
  [UPDATE_LANG]: handleUpdateLang,
  [SET_SESSION_DATA]: handleSetSessionData,
});
